import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import BoltIcon from "@mui/icons-material/Bolt";
import PaymentsIcon from "@mui/icons-material/Payments";
import PlaceIcon from "@mui/icons-material/Place";
import ReplayIcon from "@mui/icons-material/Replay";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import dayjs from "dayjs";

import { Agendamiento, OrdenTrabajo } from "@/shared/interfaces";
import { getAgendamiento } from "@/shared/actions/erp";
import FotosTrasladoTab from "@/erp/tecnico/orden-trabajo/shared/components/tables/fotos/traslado/FotosTrasladoTab";
import ConceptosTrasladoSection from "../otros-conceptos-traslado/ConceptosTrasladoSection";
import ExcedenteFibraTraslado from "../otros-conceptos-traslado/ExcedenteFibraTraslado";
import { excedenteFibraDe } from "../otros-conceptos-traslado/excedente-fibra.utils";
import { aprobacionDe, resumenConceptos } from "../otros-conceptos-traslado/conceptos-traslado.utils";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

const ACCENT = "#1565c0";

function fmt(value?: string | null): string {
  return value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "—";
}

export function duracionTraslado(
  inicio?: string | null,
  fin?: string | null
): string {
  if (!inicio || !fin) return "—";
  const mins = dayjs(fin).diff(dayjs(inicio), "minute");
  if (mins < 0) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function SeccionTitulo({ icon, title }: { icon: ReactNode; title: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      <Box sx={{ color: ACCENT, display: "flex" }}>{icon}</Box>
      <Typography variant="subtitle2" fontWeight={700}>
        {title}
      </Typography>
    </Stack>
  );
}

function Campo({ label, value }: { label: string; value?: ReactNode }) {
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
        {value || value === 0 ? value : "—"}
      </Typography>
    </Grid>
  );
}

/** Un paso de la línea de tiempo (fecha + etiqueta + quién). */
function PasoTimeline({
  label,
  fecha,
  por,
  ultimo = false,
}: {
  label: string;
  fecha?: string | null;
  por?: string;
  ultimo?: boolean;
}) {
  const done = !!fecha;
  return (
    <Box sx={{ display: "flex", gap: 1.5 }}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <FiberManualRecordIcon
          sx={{ fontSize: 14, color: done ? ACCENT : "text.disabled" }}
        />
        {!ultimo && (
          <Box
            sx={{
              flex: 1,
              width: "2px",
              minHeight: 22,
              bgcolor: done ? alpha(ACCENT, 0.35) : "divider",
            }}
          />
        )}
      </Box>
      <Box sx={{ pb: ultimo ? 0 : 1.5 }}>
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        <Typography variant="caption" color={done ? "text.primary" : "text.secondary"}>
          {fmt(fecha)}
          {por ? `  ·  ${por}` : ""}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Dossier de cierre de un TRASLADO finalizado: línea de tiempo completa
 * (creación → agendado → inicio → fin), técnico/flota, activaciones,
 * recoordinación, valores/recaudación, ubicación y las 3 galerías de fotos.
 *
 * Los tiempos del técnico y las fotos salen de la Orden de Trabajo; los datos
 * de creación, flota, valores/recaudación y ubicación anterior salen del
 * Agendamiento (se resuelve por su id para asegurar `observaciones`).
 */
export default function TrasladoFinalizadoDetalle({ ot }: { ot: OrdenTrabajo }) {
  const [ag, setAg] = useState<Agendamiento | null>(
    (ot?.agendamiento_data as Agendamiento) ?? null
  );

  useEffect(() => {
    let active = true;
    const yaTieneObs = !!(ot?.agendamiento_data as any)?.observaciones;
    if (yaTieneObs) {
      setAg(ot.agendamiento_data as Agendamiento);
      return;
    }
    const agId = ot?.agendamiento;
    if (!agId) {
      setAg((ot?.agendamiento_data as Agendamiento) ?? null);
      return;
    }
    (async () => {
      try {
        const res = await getAgendamiento(agId);
        if (active && (res as any)?.code === 200) {
          setAg((res as any).data as Agendamiento);
        }
      } catch {
        /* conservamos lo que haya */
      }
    })();
    return () => {
      active = false;
    };
  }, [ot?.agendamiento]);

  // ---- Derivados de valores / recaudación (desde el agendamiento) ----
  const obs = (ag as any)?.observaciones || {};
  const rubros = obs.rubros_cobro || {};
  const ubicacionAnterior = obs.ubicacion_anterior || {};
  const flotaData = (ot?.flota_data as any) || (ag as any)?.flota_data;

  const esFibrapoints =
    rubros.puntos_canjeados != null &&
    `${rubros.puntos_canjeados}`.trim() !== "" &&
    Number(rubros.puntos_canjeados) > 0;

  const metodoRecaudacion = esFibrapoints
    ? "FibraPoints"
    : obs.recaudacion_tecnico
    ? "Recaudación técnico"
    : obs.cargo_a_factura
    ? "Cargo a factura"
    : "—";

  const valorTraslado =
    obs.valor_traslado != null && `${obs.valor_traslado}`.trim() !== ""
      ? `$${obs.valor_traslado}`
      : "—";

  const tieneValores = metodoRecaudacion !== "—" || valorTraslado !== "—";
  const tieneUbicacionAnterior =
    !!ubicacionAnterior?.coordenadas || !!ubicacionAnterior?.direccion;

  const dur = duracionTraslado(
    ot?.fecha_hora_inicio_instalacion,
    ot?.fecha_hora_fin_instalacion
  );

  const otObs = (ot?.observaciones as any) || {};
  const asesorCreador = (ag as any)?.user_create;
  const cliente =
    (ot?.contrato_data as any)?.cliente_data?.razon_social ||
    (ot as any)?.prospecto_data?.razon_social;
  const identificacion =
    (ot as any)?.prospecto_data?.identificacion ||
    (ot?.contrato_data as any)?.identificacion;

  const fechaAgendada = useMemo(() => {
    const f = (ag as any)?.fecha_instalacion;
    const h = (ag as any)?.hora_instalacion;
    if (!f) return null;
    return h ? `${f}T${h}` : f;
  }, [ag]);

  return (
    <Grid container spacing={3}>
      {/* ===== Línea de tiempo del proceso ===== */}
      <Grid item xs={12} md={6}>
        <SeccionTitulo
          icon={<AccessTimeIcon fontSize="small" />}
          title="Línea de tiempo del proceso"
        />
        <Box sx={{ pl: 0.5 }}>
          <PasoTimeline
            label="Orden de servicio creada"
            fecha={(ag as any)?.fecha_creacion || ot?.fecha_creacion}
            por={asesorCreador}
          />
          <PasoTimeline label="Agendado para" fecha={fechaAgendada} />
          <PasoTimeline
            label="Inicio del traslado (técnico)"
            fecha={ot?.fecha_hora_inicio_instalacion}
            por={ot?.user_create || undefined}
          />
          <PasoTimeline
            label="Traslado finalizado"
            fecha={ot?.fecha_hora_fin_instalacion}
            ultimo
          />
        </Box>
        <Box sx={{ mt: 1 }}>
          <Chip
            size="small"
            label={`Duración: ${dur}`}
            sx={{ bgcolor: alpha(ACCENT, 0.1), color: ACCENT, fontWeight: 700 }}
          />
        </Box>
      </Grid>

      {/* ===== Cliente ===== */}
      <Grid item xs={12} md={6}>
        <SeccionTitulo icon={<PersonIcon fontSize="small" />} title="Cliente" />
        <Grid container spacing={1.5}>
          <Campo label="Cliente" value={cliente} />
          <Campo label="Identificación" value={identificacion} />
          <Campo
            label="Contrato"
            value={(ot?.contrato_data as any)?.numero_referencia}
          />
          <Campo
            label="Estado de la orden"
            value={
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={ot?.estado_orden_trabajo || "REALIZADA"}
              />
            }
          />
        </Grid>
      </Grid>

      {/* ===== Técnico / Flota ===== */}
      <Grid item xs={12} md={6}>
        <SeccionTitulo
          icon={<LocalShippingIcon fontSize="small" />}
          title="Técnico / Flota"
        />
        <Grid container spacing={1.5}>
          <Campo label="Flota / Cuadrilla" value={flotaData?.nombre} />
          <Campo label="Técnico líder" value={flotaData?.lider_data?.razon_social} />
          <Campo label="Auxiliar" value={flotaData?.auxiliar} />
          <Campo label="Teléfono flota" value={flotaData?.telefono_1} />
        </Grid>
      </Grid>

      {/* ===== Activaciones ===== */}
      <Grid item xs={12} md={6}>
        <SeccionTitulo
          icon={<BoltIcon fontSize="small" />}
          title="Respuesta de Activaciones"
        />
        <Grid container spacing={1.5}>
          <Campo
            label="Estado de activación"
            value={
              <Chip
                size="small"
                variant="outlined"
                label={ot?.estado_activacion || "—"}
              />
            }
          />
          <Campo label="Última activación" value={fmt(ot?.fecha_ultima_activacion)} />
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Observación de activaciones
            </Typography>
            <Typography variant="body2">
              {otObs?.observacion_activacion || "—"}
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      {/* ===== Recoordinación ===== */}
      <Grid item xs={12} md={6}>
        <SeccionTitulo icon={<ReplayIcon fontSize="small" />} title="Recoordinación" />
        <Grid container spacing={1.5}>
          <Campo
            label="¿Hubo recoordinación?"
            value={
              <Chip
                size="small"
                color={ot?.recoordination_required ? "warning" : "default"}
                variant="outlined"
                label={ot?.recoordination_required ? "SÍ" : "No"}
              />
            }
          />
          <Campo label="Motivo / código" value={ot?.motivo_rechazo} />
          {otObs?.observacion_recoordinar && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Observación de recoordinación
              </Typography>
              <Typography variant="body2">{otObs.observacion_recoordinar}</Typography>
            </Grid>
          )}
        </Grid>
      </Grid>

      {/* ===== Valores / Recaudación ===== */}
      {tieneValores && (
        <Grid item xs={12} md={6}>
          <SeccionTitulo
            icon={<PaymentsIcon fontSize="small" />}
            title="Valores / Recaudación"
          />
          <Grid container spacing={1.5}>
            <Campo label="Método de recaudación" value={metodoRecaudacion} />
            <Campo label="Valor traslado" value={valorTraslado} />
            {esFibrapoints && (
              <Campo
                label="FibraPoints antes / canjeados"
                value={`${rubros.puntos_antes_canje ?? "—"} / ${
                  rubros.puntos_canjeados ?? "—"
                }`}
              />
            )}
            {obs.cargo_a_factura && rubros.meses_pago != null && (
              <Campo label="Meses a diferir" value={rubros.meses_pago} />
            )}
            {rubros.id_contrato_softnet && (
              <Campo label="Contrato Softnet" value={rubros.id_contrato_softnet} />
            )}
          </Grid>
        </Grid>
      )}

      {/* ===== Ubicación ===== */}
      <Grid item xs={12} md={6}>
        <SeccionTitulo icon={<PlaceIcon fontSize="small" />} title="Ubicación" />
        <Grid container spacing={1.5}>
          {tieneUbicacionAnterior && (
            <>
              <Campo label="Origen · coordenadas" value={ubicacionAnterior?.coordenadas} />
              <Campo label="Origen · dirección" value={ubicacionAnterior?.direccion} />
            </>
          )}
          <Campo
            label="Destino · coordenadas"
            value={(ag as any)?.coordenadas || ot?.coordenadas}
          />
          <Campo label="Destino · dirección" value={(ag as any)?.direccion} />
        </Grid>
      </Grid>

      {/* ===== Otros conceptos + puntos de red del traslado (solo lectura) ===== */}
      {(Array.isArray(obs.otros_conceptos) && obs.otros_conceptos.length > 0) ||
      (Array.isArray(obs.puntos_red) && obs.puntos_red.length > 0) ||
      excedenteFibraDe(obs) ? (
        <Grid item xs={12}>
          <Divider sx={{ mb: 2 }} />
          <ConceptosTrasladoSection
            conceptos={obs.otros_conceptos || []}
            puntosRed={obs.puntos_red || []}
            readOnly
            aprobacion={aprobacionDe(obs)}
            despuesDePuntosRed={
              excedenteFibraDe(obs) && (
                <ExcedenteFibraTraslado excedente={excedenteFibraDe(obs)} modo="lectura" />
              )
            }
            subtitle="Equipos/servicios adicionales cobrados en el traslado (se envían a Softnet al finalizar)."
          />
          {/* Trazabilidad de cambios de conceptos / puntos de red */}
          {(() => {
            const hist: any[] = [
              ...((obs.otros_conceptos_historial || []) as any[]).map((h) => ({ ...h, _tipo: "Conceptos" })),
              ...((obs.puntos_red_historial || []) as any[]).map((h) => ({ ...h, _tipo: "Puntos de red" })),
            ].sort((a, b) => String(a.fecha || "").localeCompare(String(b.fecha || "")));
            if (!hist.length) return null;
            return (
              <Box sx={{ mt: 1.5 }}>
                <SeccionTitulo
                  icon={<Inventory2OutlinedIcon fontSize="small" />}
                  title="Trazabilidad de conceptos"
                />
                <Stack spacing={0.75}>
                  {hist.map((h, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={h._tipo} variant="outlined" />
                      <Chip size="small" label={h.origen || "—"} color="primary" variant="outlined" />
                      <Typography variant="caption" color="text.secondary">
                        {fmt(h.fecha)} · {h.usuario || "—"}
                      </Typography>
                      <Typography variant="caption">
                        {h._tipo === "Conceptos"
                          ? `${resumenConceptos(h.anterior).join(", ") || "sin conceptos"} → ${resumenConceptos(h.nuevo).join(", ") || "sin conceptos"}`
                          : `${(h.anterior || []).length} punto(s) → ${(h.nuevo || []).length} punto(s)`}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            );
          })()}
        </Grid>
      ) : null}

      {/* ===== Fotos (ingreso / proceso / finalización) ===== */}
      <Grid item xs={12}>
        <Divider sx={{ mb: 2 }} />
        <SeccionTitulo
          icon={<PhotoLibraryIcon fontSize="small" />}
          title="Evidencia fotográfica"
        />
        <FotosTrasladoTab ordenTrabajo={ot} readOnly />
      </Grid>
    </Grid>
  );
}
